import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, X } from "lucide-react";
import { toast } from "sonner";

export default function Favorites() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data: ids } = await api.get("/favorites");
      if (ids.length === 0) { setProducts([]); return; }
      const all = await api.get("/products?limit=500");
      setProducts(all.data.filter((p) => ids.includes(p.id)));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  const remove = async (productId) => {
    try {
      await api.delete(`/favorites/${productId}`);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      toast.success("Removido dos favoritos");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (authLoading) {
    return <div className="max-w-md mx-auto px-4 py-16 text-center text-neutral-500">Carregando...</div>;
  }

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center space-y-4">
      <Heart className="h-12 w-12 mx-auto text-neutral-300" />
      <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Favoritos</h1>
      <p className="text-neutral-500">Entre na sua conta para salvar produtos favoritos.</p>
      <Link to="/entrar?next=/favoritos"><Button data-testid="favorites-login-btn" className="bg-[#C91F1F] hover:bg-[#9F1515]">Entrar</Button></Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-10">
      <h1 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Meus favoritos</h1>
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[4/5] bg-neutral-100 rounded-xl animate-pulse" />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 space-y-4">
          <Heart className="h-14 w-14 mx-auto text-neutral-300" />
          <p className="text-neutral-500">Você ainda não tem produtos favoritos.</p>
          <Link to="/"><Button variant="outline">Explorar loja</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
          {products.map((p) => (
            <div key={p.id} data-testid={`favorite-item-${p.id}`} className="relative">
              <ProductCard product={p} />
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); remove(p.id); }}
                data-testid={`favorite-remove-${p.id}`}
                aria-label="Remover dos favoritos"
                className="absolute top-2 right-2 z-10 h-9 w-9 rounded-full bg-white/95 shadow-md border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-[#C91F1F] hover:border-[#C91F1F] transition"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
